import { DownOutlined, UpOutlined, UserOutlined } from '@ant-design/icons'
import { Box, Typography } from '@mui/material'
import type { Theme } from '@mui/system'
import { styled } from '@mui/system'
import Paper from 'components/@extended/Paper'
import type { FC } from 'react'
import { calculateNodeWidth, getManagerName } from '../utils'
import type { TreeNode as TreeNodeType } from '../hooks/useTreeState'

const NodeContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  position: 'relative',
  width: '100%',
  height: '100%',
}))

interface TreeNodeProps {
  nodeData: TreeNodeType
  isCollapsed: boolean
  isHovered: boolean
  hasChildren: boolean
  childCount: number
  theme: Theme
  onToggleCollapse: (e: React.MouseEvent) => void
  onNodeSelect: () => void
  onAddTeam: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const TreeNode: FC<TreeNodeProps> = ({
  nodeData,
  isCollapsed,
  isHovered,
  hasChildren,
  childCount,
  theme,
  onToggleCollapse,
  onNodeSelect,
  onAddTeam,
  onMouseEnter,
  onMouseLeave,
}) => {
  const width = calculateNodeWidth(nodeData.name)
  const displayName = nodeData.visible ? nodeData.name : getManagerName(nodeData.users) || 'Manager Name'

  return (
    <foreignObject width={width} height={100} x={-width / 2} y={-50}>
      <NodeContainer onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <Paper
          sx={{
            pt: 2,
            borderRadius: '8px',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.text.secondary,
              fontSize: '10px',
            }}
          >
            <UserOutlined style={{ marginRight: '2px' }} />
            {nodeData.userCount}
          </Box>
          {hasChildren && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                cursor: 'pointer',
                zIndex: 1,
                color: theme.palette.text.secondary,
                opacity: 0.6,
                padding: '10px',
              }}
              onClick={onToggleCollapse}
            >
              {isCollapsed ? <DownOutlined /> : <UpOutlined />}
            </Box>
          )}
          <Typography
            variant="h6"
            noWrap
            align="center"
            sx={{
              mb: 0.5,
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              },
              maxWidth: '90%',
            }}
            onClick={onNodeSelect}
          >
            {displayName}
          </Typography>
          {hasChildren && isCollapsed ? (
            <Typography variant="caption" color="primary">
              {childCount} child
              {childCount !== 1 ? 'ren' : ''}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.2s',
                color: 'primary.main',
                cursor: 'pointer',
              }}
              onClick={onAddTeam}
            >
              + Add Team
            </Typography>
          )}
        </Paper>
      </NodeContainer>
    </foreignObject>
  )
}

export default TreeNode
